import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';

export default function GoalEditor() {
  const navigate = useNavigate();
  const { goalId } = useParams();
  const isEditing = !!goalId;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalType, setGoalType] = useState<'checkbox' | 'numeric'>('checkbox');
  const [targetValue, setTargetValue] = useState('');
  const [unit, setUnit] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      api.getGoals()
        .then((res: any) => {
          const goal = res.goals.find((g: any) => String(g.id) === goalId);
          if (goal) {
            setTitle(goal.title);
            setDescription(goal.description || '');
            setGoalType(goal.goal_type);
            setTargetValue(goal.target_value ? String(goal.target_value) : '');
            setUnit(goal.unit || '');
            setFrequency(goal.frequency);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [isEditing, goalId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSaving(true);
    try {
      const data = {
        title: title.trim(),
        description: description.trim() || undefined,
        goal_type: goalType,
        target_value: goalType === 'numeric' && targetValue ? parseFloat(targetValue) : undefined,
        unit: goalType === 'numeric' && unit.trim() ? unit.trim() : undefined,
        frequency,
      };

      if (isEditing) {
        await api.updateGoal(goalId!, data);
      } else {
        await api.createGoal(data);
      }
      navigate(-1);
    } catch (err) {
      alert('Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this goal and all its progress?')) return;
    try {
      await api.deleteGoal(goalId!);
      navigate(-1);
    } catch (err) {
      alert('Failed to delete goal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-4">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-white mr-3 text-lg"
        >
          ←
        </button>
        <h1 className="text-xl font-bold">{isEditing ? 'Edit Goal' : 'New Goal'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Read 30 pages"
            className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3.5 px-4 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Why is this goal important?"
            rows={3}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3.5 px-4 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Goal Type</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setGoalType('checkbox')}
              className={`py-3 px-4 rounded-xl text-sm font-medium border transition-all ${
                goalType === 'checkbox'
                  ? 'bg-primary-600/20 border-primary-500 text-primary-400'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              ✅ Checkbox
            </button>
            <button
              type="button"
              onClick={() => setGoalType('numeric')}
              className={`py-3 px-4 rounded-xl text-sm font-medium border transition-all ${
                goalType === 'numeric'
                  ? 'bg-primary-600/20 border-primary-500 text-primary-400'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              🔢 Numeric
            </button>
          </div>
        </div>

        {goalType === 'numeric' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Target Value</label>
              <input
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="30"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3.5 px-4 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Unit</label>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="pages"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3.5 px-4 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Frequency</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFrequency('daily')}
              className={`py-3 px-4 rounded-xl text-sm font-medium border transition-all ${
                frequency === 'daily'
                  ? 'bg-primary-600/20 border-primary-500 text-primary-400'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              📅 Daily
            </button>
            <button
              type="button"
              onClick={() => setFrequency('weekly')}
              className={`py-3 px-4 rounded-xl text-sm font-medium border transition-all ${
                frequency === 'weekly'
                  ? 'bg-primary-600/20 border-primary-500 text-primary-400'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              📆 Weekly
            </button>
          </div>
        </div>

        <div className="pt-4 space-y-3">
          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="w-full bg-primary-600 hover:bg-primary-500 text-white font-semibold py-4 rounded-xl transition-colors shadow-lg shadow-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transform"
          >
            {saving ? 'Saving...' : isEditing ? 'Update Goal' : 'Create Goal'}
          </button>

          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 font-medium py-3.5 rounded-xl transition-colors border border-red-500/20"
            >
              Delete Goal
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
